import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import config from "@/config";

import { getArtworkHoldingContext } from "../utils/artworkView.selectors";

export function normaliseArtworkRouteParam(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const str = `${value}`.trim();
  if (str.length === 0) {
    return null;
  }
  if (str.toLowerCase() === "null") {
    return null;
  }
  return str;
}

export function resolveArtworkHoldingContext(
  artwork,
  {
    fallbackHoldingId = null,
    fallbackOwnerId = null,
    fallbackHoldingType = null,
  } = {},
) {
  const baseHoldingContext = getArtworkHoldingContext(artwork);
  const holdingCandidates = [
    baseHoldingContext.holdingId,
    artwork?.product_id,
    artwork?.productId,
    artwork?.product?.id,
    artwork?.product_data?.id,
    artwork?.metadata?.limited_edition?.copy_id,
    artwork?.product_metadata?.limited_edition?.copy_id,
    fallbackHoldingId,
  ];
  const holdingIdCandidate = holdingCandidates
    .map((candidate) => normaliseArtworkRouteParam(candidate))
    .find(Boolean) ?? null;

  let ownerIdCandidate = normaliseArtworkRouteParam(baseHoldingContext.ownerId);
  const owner = artwork?.owner ?? artwork?.product_owner ?? null;
  if (!ownerIdCandidate && typeof owner === "string") {
    ownerIdCandidate = normaliseArtworkRouteParam(owner);
  } else if (!ownerIdCandidate && owner && typeof owner === "object") {
    ownerIdCandidate = normaliseArtworkRouteParam(owner.id ?? owner._id ?? owner.owner_id ?? owner.ownerId);
  }
  if (!ownerIdCandidate) {
    ownerIdCandidate = normaliseArtworkRouteParam(fallbackOwnerId);
  }

  const holdingTypeCandidate =
    normaliseArtworkRouteParam(baseHoldingContext.holdingType)
    ?? normaliseArtworkRouteParam(fallbackHoldingType);

  return {
    holdingId: holdingIdCandidate,
    ownerId: ownerIdCandidate,
    holdingType: holdingTypeCandidate,
  };
}

export function buildArtworkEndpointForRoute({
  slugValue,
  holdingId = null,
  holdingOwnerId = null,
  holdingTypeParam = null,
  requestedViewParam = null,
  options = {},
}) {
  const shouldUseRouteContext = options.useRouteContext !== false;
  const params = new URLSearchParams();
  const resolvedHoldingId = normaliseArtworkRouteParam(options.holdingId) ?? (shouldUseRouteContext ? holdingId : null);
  const resolvedOwnerId = normaliseArtworkRouteParam(options.ownerId) ?? (shouldUseRouteContext ? holdingOwnerId : null);
  const resolvedHoldingType = normaliseArtworkRouteParam(options.holdingType) ?? (shouldUseRouteContext ? holdingTypeParam : null);
  const resolvedView = normaliseArtworkRouteParam(options.view)
    ?? (resolvedHoldingId ? "holding" : (shouldUseRouteContext ? requestedViewParam : null));

  if (resolvedHoldingId) {
    params.set("holding_id", resolvedHoldingId);
  }
  if (resolvedOwnerId) {
    params.set("owner_id", resolvedOwnerId);
  }
  if (resolvedHoldingType) {
    params.set("holding_type", resolvedHoldingType);
  }
  if (resolvedView) {
    params.set("view", resolvedView);
  }

  const queryString = params.toString();
  return `${config.apis.api.url}/artwork/${slugValue}${queryString ? `?${queryString}` : ""}`;
}

export default function useArtworkRouteContext() {
  const { slug } = useParams();
  const location = useLocation();
  const viewContextFromState = location.state?.artworkViewContext ?? null;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const holdingId = normaliseArtworkRouteParam(searchParams.get("holding_id")) ?? normaliseArtworkRouteParam(viewContextFromState?.holdingId);
  const holdingOwnerId = normaliseArtworkRouteParam(searchParams.get("owner_id")) ?? normaliseArtworkRouteParam(viewContextFromState?.ownerId);
  const holdingTypeParam = normaliseArtworkRouteParam(searchParams.get("holding_type")) ?? normaliseArtworkRouteParam(viewContextFromState?.holdingType);
  const requestedViewParam = normaliseArtworkRouteParam(searchParams.get("view")) ?? normaliseArtworkRouteParam(viewContextFromState?.type);

  const buildArtworkEndpoint = (slugValue, options = {}) => buildArtworkEndpointForRoute({
    slugValue,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    requestedViewParam,
    options,
  });

  return {
    slug,
    location,
    viewContextFromState,
    searchParams,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    requestedViewParam,
    buildArtworkEndpoint,
    resolveHoldingContext: (artwork, fallbackHoldingId = null, fallbackOwnerId = null, fallbackHoldingType = null) => (
      resolveArtworkHoldingContext(artwork, {
        fallbackHoldingId,
        fallbackOwnerId,
        fallbackHoldingType,
      })
    ),
  };
}
